/**
 * PLT-5889: entry point for connecting Upright's MCP server to the user's own
 * AI client.
 *
 * Rendered in four places. In the legacy chat, deliberately both the pinned
 * footer of the full-page `/chat` history sidebar and the "Ask AI" side
 * panel's empty state: usage of the two chat surfaces is roughly even and the
 * populations barely overlap, so a single placement would reach about half
 * the audience. In the unified shell (PLT-8002), the foot of the rail on every
 * page that has the app rail, and the far end of the privacy line under the
 * launcher's composer, because the unified shell mounts no history sidebar.
 *
 * Gated on `USE_AI_CHAT_CONNECTOR`, which is a *separate* privilege from the
 * `USE_AI_CHAT_WEB` gate on the chat itself. A user can hold chat access
 * without connector access, and for them the consent page answers with "not
 * enabled for your account" — so hiding the entry keeps us from advertising a
 * dead end.
 *
 * The popover is the entire setup UI, and that is not a shortcut: the
 * connector authenticates over OAuth 2.1 + PKCE with dynamic client
 * registration against the user's existing login, so there is no key to issue
 * and nothing to configure beyond the server URL.
 */
import { useRef, useState } from 'react';
import { Heading } from 'react-aria-components';
import styled from 'styled-components';
import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { LinkButton } from '@upright/ui/common/generic/components/button';
import { CopyToClipboardButton } from '@upright/ui/common/generic/components/copy-to-clipboard-button';
import { Link } from '@upright/ui/common/generic/components/link';
import { Popover } from '@upright/ui/common/generic/components/popover';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import {
  type ChatConnectorEntryPoint,
  CustomGtmEvent,
} from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { ChatGptMark, ClaudeMark } from '@upright/ui/common/upright/assistant-marks';
import { KBUrl } from '@upright/ui/common/upright/kb-urls';
import { ApplicationAdd } from '@upright/ui/icons';
import { getMcpServerUrl } from '@upright/ui/untidy/urls';

/**
 * Two client names rather than the full supported list (which also includes
 * Gemini, Mistral Le Chat and Microsoft Copilot): naming the two most common
 * ones makes the row instantly concrete for someone scanning past it, where
 * "connect your AI assistant" reads as jargon. The setup guide carries the
 * fuller list.
 */
const ENTRY_LABEL = 'Use in Claude or ChatGPT';

/*
 * Where Claude and ChatGPT add a custom connector. They open in a new tab, as `LinkButton` opens every external link, so the URL the
 * reader just copied is still on the clipboard when the page arrives.
 */
const CLAUDE_ADD_CONNECTOR_URL = 'https://claude.ai/new?modal=add-custom-connector#customize/connectors/yours';
const CHATGPT_ADD_CONNECTOR_URL = 'https://chatgpt.com/plugins';

/**
 * Where this instance is rendered. One prop rather than a pair of booleans
 * because the cases are mutually exclusive, and because the analytics entry
 * point and the popover's side follow from the placement rather than being
 * chosen separately at each call site.
 */
type Variant =
  /** Expanded 260px history sidebar footer: a full-width row with a label. */
  | 'sidebar'
  /** Collapsed 56px rail: the icon alone, label moved to the accessible name. */
  | 'sidebar-collapsed'
  /** "Ask AI" side panel empty state: an auto-width row, centred by its wrapper. */
  | 'side-panel'
  /** The unified shell's rail foot: a rail row, with the two assistants' marks in its icon column. */
  | 'rail'
  /** The same rail row in the phone drawer the rail becomes below `md`. */
  | 'drawer'
  /** The launcher's privacy line: an auto-width link at the line's far end, marks first. */
  | 'composer';

const ENTRY_POINT_BY_VARIANT: Record<Variant, ChatConnectorEntryPoint> = {
  sidebar: 'chat-sidebar',
  'sidebar-collapsed': 'chat-sidebar',
  'side-panel': 'chat-side-panel',
  rail: 'unified-rail',
  drawer: 'unified-rail',
  composer: 'launcher',
};

/**
 * The rail's popover opens beside the rail rather than over the rows above the
 * trigger. The phone drawer leaves no room on either side of itself, so there
 * it opens above the row, where the screen has room. The launcher's opens under
 * the link, at the column's far end, where the link sits.
 */
const PLACEMENT_BY_VARIANT = {
  sidebar: 'top start',
  'sidebar-collapsed': 'top start',
  'side-panel': 'top start',
  rail: 'right bottom',
  drawer: 'top start',
  composer: 'bottom end',
} as const satisfies Record<Variant, string>;

interface Props {
  variant: Variant;
}

export function ChatConnectorEntry({ variant }: Props) {
  const { hasPrivilege, loading } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_AI_CHAT_CONNECTOR);
  const pushGtmEvent = usePushGtmEvent();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Hide while the privilege check is in flight, so the row doesn't pop in a
  // beat after the rest of the sidebar has painted.
  if (loading || !hasPrivilege) return null;

  const entryPoint = ENTRY_POINT_BY_VARIANT[variant];
  const iconOnly = variant === 'sidebar-collapsed';
  const isRailRow = variant === 'rail' || variant === 'drawer';
  const withMarks = isRailRow || variant === 'composer';
  const mcpServerUrl = getMcpServerUrl();

  return (
    <>
      <TriggerButton
        ref={triggerRef}
        type="button"
        data-variant={variant}
        aria-label={iconOnly ? ENTRY_LABEL : undefined}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => {
          const willOpen = !isOpen;
          setIsOpen(willOpen);
          // Only the opening half is interesting — a close is just dismissal.
          if (willOpen) {
            pushGtmEvent({ event: CustomGtmEvent.CHAT_CONNECTOR_OPEN, data: { entryPoint } });
          }
        }}
      >
        {withMarks ? (
          <Marks aria-hidden="true">
            <ClaudeMark />
            <ChatGptMark />
          </Marks>
        ) : (
          <ApplicationAdd width={14} height={14} aria-hidden="true" />
        )}
        {!iconOnly && <TriggerLabel $wraps={isRailRow}>{ENTRY_LABEL}</TriggerLabel>}
      </TriggerButton>
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        triggerRef={triggerRef}
        placement={PLACEMENT_BY_VARIANT[variant]}
      >
        <PopoverBody>
          {/* Heading and body are grouped so the gap inside the pair is tighter
           * than the gap to the steps below — otherwise every child sits on one
           * even rhythm and the popover reads as a flat list. */}
          <PopoverIntro>
            {/* `slot="title"` is what names the surrounding react-aria Dialog —
             * the shared Popover takes no `aria-label`, so without this the
             * dialog would be announced unlabelled. */}
            <PopoverHeading slot="title" level={2}>
              Use Upright in Claude or ChatGPT
            </PopoverHeading>
            <PopoverText>Ask about any company or fund from the assistant you already use.</PopoverText>
          </PopoverIntro>
          <Steps>
            <Step>
              <StepLabel>1. Copy the connector URL</StepLabel>
              <UrlWell>
                <UrlText>{mcpServerUrl}</UrlText>
                {/* Icon-only, which is what `CopyToClipboardButton` renders when it
                 * is given no children — and it is what keeps the URL on one line
                 * (see `PopoverBody`). It still carries the component's default
                 * `aria-label="Copy"` and its "Copied!" confirmation, so nothing is
                 * lost but the word. Outlined rather than text, because borderless
                 * it read as decoration rather than as the control this popover
                 * exists for. */}
                <CopyToClipboardButton
                  variant="outlined"
                  size="small"
                  textToCopy={mcpServerUrl}
                  showTooltipOnSuccess
                  onCopySuccess={() => {
                    pushGtmEvent({ event: CustomGtmEvent.CHAT_CONNECTOR_URL_COPY, data: { entryPoint } });
                  }}
                />
              </UrlWell>
            </Step>
            <Step>
              {/* Signing in is the connector's OAuth flow against the user's own
               * Upright login, so there is no key to hand over in this step. */}
              <StepLabel>2. Add it as a custom connector, then sign in</StepLabel>
              <OpenRow>
                <LinkButton variant="outlined" size="small" startIcon={ClaudeMark} href={CLAUDE_ADD_CONNECTOR_URL}>
                  Open Claude
                </LinkButton>
                <LinkButton variant="outlined" size="small" startIcon={ChatGptMark} href={CHATGPT_ADD_CONNECTOR_URL}>
                  Open ChatGPT
                </LinkButton>
              </OpenRow>
            </Step>
          </Steps>
          <SetupGuideRow>
            {/* `color="inherit"` is the only colour escape hatch `Link` offers,
             * and it is the one that works: it makes `Link.module.css` set
             * `color: inherit` on the anchor, so the colour comes from this
             * wrapper instead of from a styled-components rule that would lose
             * to the module's `:is(:link, :active, :visited)` selector. */}
            <Link
              color="inherit"
              href={KBUrl.AGENT_EXTERNAL_CLIENTS_MCP}
              onPress={() => {
                pushGtmEvent({ event: CustomGtmEvent.CHAT_CONNECTOR_DOCS_CLICK, data: { entryPoint } });
              }}
            >
              Setup guide
            </Link>
          </SetupGuideRow>
        </PopoverBody>
      </Popover>
    </>
  );
}

/*
 * Quiet secondary chrome, matching the "+ New chat" row above it in the
 * sidebar: 14px, `gray-600`, medium weight, with the same `gray-100` hover
 * pill the conversation rows use. `gray-600` (not `gray-500`) is the muted
 * text tier that clears WCAG AA — the row is meant to be easy to overlook,
 * not hard to read.
 */
const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  width: 100%;
  padding: var(--space__sm) var(--space__md);
  min-height: 36px;
  border: none;
  background: transparent;
  font-family: var(--font-family__primary);
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__medium);
  line-height: 1.3;
  color: var(--color__gray-600);
  text-align: left;
  border-radius: var(--border-radius__md);
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover {
    background-color: var(--color__gray-100);
    color: var(--color__gray-900);
  }

  &:focus-visible {
    outline: var(--focus-ring__width) solid var(--focus-ring__color);
    outline-offset: -2px;
  }

  & > svg {
    flex-shrink: 0;
  }

  /* Collapsed rail: a centred 28px icon button, same shape as the rail's
   * chevron and plus buttons. */
  &[data-variant='sidebar-collapsed'] {
    width: 28px;
    height: 28px;
    min-height: 0;
    padding: 0;
    justify-content: center;
  }

  /* Side panel: sized to its label rather than the panel width, so it reads as
   * one quiet line under the centred suggestion chips instead of a full-width
   * band competing with them. */
  &[data-variant='side-panel'] {
    width: auto;
    font-size: var(--font-size__sm);
  }

  /* The unified rail's foot: one of the rail's own rows, so its box is the
   * shared navRowBox (sm by lg padding, full radius) and its label the rail
   * rows' gray-900. It is a utility of the rail, not a footnote under it. The
   * size is inherited from the foot, which takes the rows' own step
   * (unified-shell-frame.module.css), so it drops to 12px with them. */
  &[data-variant='rail'],
  &[data-variant='drawer'] {
    padding: var(--space__sm) var(--space__lg);
    min-height: 0;
    font-size: inherit;
    color: var(--color__gray-900);
    line-height: var(--line-height__sm);
    border-radius: var(--border-radius__full);
  }

  /* The launcher's privacy line: the line's own 14px medium gray-600, so the
   * two ends of the row read alike, with a pill only as tall as the line plus
   * xs, so it does not open the row up. */
  &[data-variant='composer'] {
    width: auto;
    min-height: 0;
    padding: var(--space__xs) var(--space__sm);
    line-height: var(--line-height__sm);
    border-radius: var(--border-radius__full);
  }
`;

/*
 * The two marks at the 16px icon size, `sm` apart, in the label's own ink: at `xs` the two round
 * marks read as one shape (PLT-8068).
 */
const Marks = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: var(--space__sm);

  & > svg {
    width: var(--space__lg);
    height: var(--space__lg);
  }
`;

/*
 * A rail row's label wraps rather than ending in an ellipsis: the rail is a
 * fixed 256px at its narrowest, and "Use in Claude or Ch…" hides the word that
 * says what the row is for.
 */
const TriggerLabel = styled.span<{ $wraps: boolean }>`
  min-width: 0;
  white-space: ${props => (props.$wraps ? 'normal' : 'nowrap')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

/*
 * The width is set by the URL rather than picked, and the target is the longest
 * one we serve — staging's, which at the well's 12px monospace is 332px against
 * production's 275px. Wrapping the one value this popover exists to hand over
 * is worse than a wide popover, and a wrapped URL also invites the reader to
 * wonder whether the break is part of it.
 *
 * 390px leaves 344px inside the well next to an icon-only Copy button, which
 * clears 332px. Keeping the word "Copy" on the button would cost another 31px
 * and need ~421px, which is wider than the 410px "Ask AI" side panel this has
 * to sit inside — so the label is what gives way, not the URL.
 *
 * 390 rather than 400 for that same panel: at 400 the popover overhung its left
 * edge by 7px. The ceiling is the panel, and 344px of well is comfortably above
 * the widest mono fallback measured (SFMono, DejaVu and Liberation Mono all sit
 * at ~0.6em per character; Consolas is narrower).
 *
 * On a phone 390px is wider than the window, so there it narrows to leave about
 * `xl` of window either side of the popover's own `lg` padding, and the URL
 * wraps rather than running off the screen with its copy button.
 */
const PopoverBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
  width: 390px;
  max-width: calc(100vw - 2 * var(--space__3xl));
`;

const PopoverIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
`;

const PopoverHeading = styled(Heading)`
  margin: 0;
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
`;

const PopoverText = styled.span`
  font-size: var(--font-size__sm);
  color: var(--color__gray-600);
  line-height: 1.4;
`;

/* The two steps, `md` apart; each one's label sits `sm` over what it asks for. */
const Steps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Step = styled.li`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

const StepLabel = styled.span`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__semibold);
  color: var(--color__gray-900);
  line-height: 1.4;
`;

/*
 * Copy sits beside the URL — see `PopoverBody` for the width that makes that
 * fit on one line in every environment. `center` rather than `flex-start` so
 * that if some future host does wrap, the button stays paired with the whole
 * well instead of pinned to the first line.
 */
const UrlWell = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  padding: var(--space__xs) var(--space__xs) var(--space__xs) var(--space__sm);
  background-color: var(--color__gray-50);
  border: 1px solid var(--color__gray-200);
  border-radius: var(--border-radius__sm);
`;

/*
 * The popover is sized so this does not wrap. The wrapping rules are the
 * fallback for a host longer than today's: wrap rather than truncate, because
 * the URL is the one thing the user came here for and an ellipsis would hide
 * the part that differs. `anywhere` rather than `break-word` so it breaks
 * inside a long hostname instead of overflowing the well.
 */
const UrlText = styled.code`
  flex: 1;
  min-width: 0;
  /* --font-family__monospace is the real token; --font-family__mono appears in
   * a few places in this codebase but is not defined anywhere. */
  font-family: var(--font-family__monospace);
  font-size: var(--font-size__sm);
  line-height: 1.4;
  color: var(--color__gray-900);
  overflow-wrap: anywhere;
`;

const OpenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space__sm);
`;

/*
 * Size and colour are set on this wrapper, not on the link: `Link` declares
 * `font: inherit` and, with `color="inherit"`, takes its colour from here too —
 * so both land without fighting `Link.module.css`. An earlier cut set them on a
 * `styled(Link)` instead and the colour silently lost to the module's
 * `:is(:link, :active, :visited)` rule (specificity 0,3,0 against a single
 * styled-components class).
 *
 * Both values are deliberate. The size, because `Link` would otherwise inherit
 * the popover's 16px default and make the least important element in the box
 * larger than the 14px heading. The colour, because `Link`'s resting default is
 * `gray-950` — 20:1 against white, heavier than the `gray-600` body copy above
 * it, which is backwards for a tertiary link. `lilac-600` is 6.5:1, so it stays
 * well clear of the low-contrast failure mode while reading as subordinate, and
 * it is the colour this surface already gives links (`.web-search-citations a`
 * in `upright-chat-theme.css`).
 *
 * The underline stays: it is the affordance, and `decoration="none"` would
 * remove the hover one along with the resting one. `align-self` is needed
 * because the flex column would otherwise stretch this row.
 */
const SetupGuideRow = styled.div`
  align-self: flex-start;
  font-size: var(--font-size__sm);
  color: var(--color__lilac-600);
`;
